import { useEffect, useMemo, useRef, useState } from 'react'
import {
  X, User, KeyRound, Settings, Sun, Moon, Monitor, Bell,
  Loader2, ShieldCheck, Lock, Smartphone, Copy, Check, AlertCircle,
  Camera, Trash2, Eye, EyeOff,
} from 'lucide-react'
import { notify } from './Toast'
import AvatarCropper from './AvatarCropper'
import TierBadge from './TierBadge'
import { useAuth } from '../context/AuthProvider'
import {
  updateMe, changePassword, disconnectProvider, uploadAvatar,
  getMyAvatarBlob, getPreferences, updatePreferences,
  twoFactorSetup, twoFactorEnable, twoFactorDisable,
  getLanguages, getCountries, getTimezones,
  type PrefsRecord, type LocaleOption,
} from '../api/client'

type Tab = 'profile' | 'account' | 'security' | 'preferences'

interface Props {
  isOpen: boolean
  initialTab?: Tab
  onClose: () => void
}

function isFederated(p?: string): boolean { return p === 'GOOGLE' || p === 'APPLE' }
function providerLabel(p?: string): string {
  if (p === 'GOOGLE') return 'Google'
  if (p === 'APPLE') return 'Apple'
  return 'your identity provider'
}

/* ──────────────────────────────────────────────────────────
 * Small internal form building blocks. Kept in this file so
 * they are colocated with the only screen using them; promote
 * to shared components when a second screen needs them.
 * ────────────────────────────────────────────────────────── */

function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: React.ReactNode }) {
  return <label htmlFor={htmlFor} className="field-label">{children}</label>
}

function FieldError({ id, message }: { id: string; message: string }) {
  return (
    <p id={id} role="alert" className="field-error">
      <AlertCircle size={12} /> {message}
    </p>
  )
}

function FieldHint({ id, children }: { id?: string; children: React.ReactNode }) {
  return <p id={id} className="field-hint">{children}</p>
}

function PasswordInput({
  id, label, value, onChange, autoComplete, error, hint, autoFocus,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  autoComplete: string
  error?: string
  hint?: string
  autoFocus?: boolean
}) {
  const [show, setShow] = useState(false)
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="relative">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          className="field-input field-input--with-action font-mono tracking-wide"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          required
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="field-action"
          aria-label={show ? 'Hide password' : 'Show password'}
          aria-pressed={show}
          tabIndex={-1}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {error ? <FieldError id={errorId} message={error} /> : hint ? <FieldHint id={hintId}>{hint}</FieldHint> : null}
    </div>
  )
}

function SectionHeader({
  icon: Icon, title, description,
}: {
  icon?: React.ElementType
  title: string
  description?: string
}) {
  return (
    <div className="flex items-start gap-3 mb-5">
      {Icon && (
        <div className="w-9 h-9 rounded-lg bg-brand-50 dark:bg-brand-900/40 text-brand-600 dark:text-brand-300 flex items-center justify-center flex-shrink-0 ring-1 ring-inset ring-brand-600/20 dark:ring-brand-400/20">
          <Icon size={18} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h3 className="settings-section-title">{title}</h3>
        {description && <p className="settings-section-description mt-1">{description}</p>}
      </div>
    </div>
  )
}

function FormActions({
  onCancel,
  submitLabel,
  submittingLabel,
  submitting,
  submitDisabled,
  variant = 'primary',
}: {
  onCancel: () => void
  submitLabel: string
  submittingLabel: string
  submitting: boolean
  submitDisabled?: boolean
  variant?: 'primary' | 'danger'
}) {
  return (
    <div className="form-actions">
      <button type="button" className="btn-secondary text-sm" onClick={onCancel} disabled={submitting}>
        Cancel
      </button>
      <button
        type="submit"
        className={`${variant === 'danger' ? 'btn-danger' : 'btn-primary'} text-sm`}
        disabled={submitting || submitDisabled}
      >
        {submitting ? <><Loader2 size={14} className="animate-spin" />{submittingLabel}</> : submitLabel}
      </button>
    </div>
  )
}

function getTabbables(container: HTMLElement | null): HTMLElement[] {
  if (!container) return []
  const sel = [
    'a[href]', 'area[href]', 'input:not([disabled])', 'select:not([disabled])',
    'textarea:not([disabled])', 'button:not([disabled])', '[tabindex]:not([tabindex="-1"])',
  ].join(',')
  return Array.from(container.querySelectorAll<HTMLElement>(sel))
    .filter((el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null)
}

/* ──────────────────────────────────────────────────────────
 * Modal
 * ────────────────────────────────────────────────────────── */

export default function AccountModal({ isOpen, initialTab = 'profile', onClose }: Props) {
  const { user, entitlements, refreshMe } = useAuth()
  const [activeTab, setActiveTab] = useState<Tab>(initialTab)
  const [submitting, setSubmitting] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = 'account-modal-title'

  useEffect(() => { if (isOpen) setActiveTab(initialTab) }, [isOpen, initialTab])

  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [isOpen])

  // Keyboard: Escape + focus trap
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) { e.stopPropagation(); onClose(); return }
      if (e.key !== 'Tab') return
      const items = getTabbables(panelRef.current)
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault() }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault() }
    }
    window.addEventListener('keydown', onKey)
    // Move focus into the panel on open
    const t = window.setTimeout(() => {
      const items = getTabbables(panelRef.current)
      items[0]?.focus()
    }, 0)
    return () => { window.removeEventListener('keydown', onKey); window.clearTimeout(t) }
  }, [isOpen, submitting, onClose])

  // Profile state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [cropSrc, setCropSrc] = useState<string | null>(null)

  const initials = useMemo(() => {
    const fn = (user?.firstName || '').trim()
    const ln = (user?.lastName || '').trim()
    const a = fn?.[0] || '', b = ln?.[0] || ''
    if (a || b) return (a + b).toUpperCase()
    return (user?.username || '?')[0].toUpperCase()
  }, [user])

  const displayName = useMemo(() => {
    const fn = (user?.firstName || '').trim()
    const ln = (user?.lastName || '').trim()
    if (fn || ln) return `${fn}${fn && ln ? ' ' : ''}${ln}`
    return user?.username || ''
  }, [user])

  useEffect(() => {
    let revokeUrl: string | undefined
    if (user && isOpen) {
      setFirstName(user.firstName || '')
      setLastName(user.lastName || '')
      setEmail(user.email || '')
      setPhoneNumber('')
      getMyAvatarBlob().then((blob) => {
        const url = URL.createObjectURL(blob)
        revokeUrl = url
        setAvatarUrl(url)
      }).catch(() => setAvatarUrl(''))
    }
    return () => { if (revokeUrl) URL.revokeObjectURL(revokeUrl) }
  }, [user, isOpen])

  // Password state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Wipe sensitive fields whenever the modal closes or tab changes.
  useEffect(() => {
    if (!isOpen) { setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setDisableConfirm('') }
  }, [isOpen])
  useEffect(() => {
    if (activeTab !== 'account') { setCurrentPassword(''); setNewPassword(''); setConfirmPassword('') }
  }, [activeTab])

  // 2FA state
  const [twoFaSetup2, setTwoFaSetup2] = useState<{ secret: string; otpauthUri: string } | null>(null)
  const [twoFaCode, setTwoFaCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [copiedRecovery, setCopiedRecovery] = useState(false)
  const [disableConfirm, setDisableConfirm] = useState('')

  // Prefs state
  const [prefs, setPrefs] = useState<PrefsRecord>({ theme: 'system', language: 'en', country: '', timezone: '', emailNotifications: true, navbarDisplay: 'avatar' })
  const [languages, setLanguages] = useState<LocaleOption[]>([])
  const [countries, setCountries] = useState<LocaleOption[]>([])
  const [timezones, setTimezones] = useState<LocaleOption[]>([])
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    getPreferences().then((p) => {
      if (cancelled || !p) return
      setPrefs({ theme: p.theme || 'system', language: p.language || 'en', country: p.country || '', timezone: p.timezone || '', emailNotifications: Boolean(p.emailNotifications), navbarDisplay: p.navbarDisplay || 'avatar' })
    }).catch(() => {})
    getLanguages().then(setLanguages).catch(() => {})
    getCountries().then(setCountries).catch(() => {})
    getTimezones().then(setTimezones).catch(() => {})
    return () => { cancelled = true }
  }, [isOpen])

  const handleAvatarFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) { notify.error('Please select an image file (PNG, JPG, or GIF).'); return }
    if (file.size > 10 * 1024 * 1024) { notify.error('The file size must not exceed 10 MB.'); return }
    const reader = new FileReader()
    reader.onload = () => { if (typeof reader.result === 'string') setCropSrc(reader.result) }
    reader.readAsDataURL(file)
  }

  const handleCroppedAvatar = async (blob: Blob) => {
    setUploadingAvatar(true)
    setCropSrc(null)
    try {
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' })
      await uploadAvatar(file)
      const fresh = await getMyAvatarBlob()
      const url = URL.createObjectURL(fresh)
      setAvatarUrl((old) => { if (old?.startsWith('blob:')) URL.revokeObjectURL(old); return url })
      notify.success('Profile picture updated.')
    } catch (_) { notify.error('Unable to upload. Please try again.') }
    finally { setUploadingAvatar(false) }
  }

  const submitProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!firstName || !lastName) { notify.error('First and last name are required.'); return }
    setSubmitting(true)
    try {
      const payload: Record<string, string> = { firstName, lastName, phoneNumber }
      if (!isFederated(user?.provider)) payload.email = email
      await updateMe(payload)
      await updatePreferences(prefs as Record<string, unknown>)
      await refreshMe()
      notify.success('Profile updated.')
    } catch (err) { notify.error((err as { message?: string })?.message || 'Update failed.') }
    finally { setSubmitting(false) }
  }

  // Password form validation — gates the submit button + inline hints.
  const newPwTooShort = newPassword.length > 0 && newPassword.length < 8
  const confirmMismatch = confirmPassword.length > 0 && confirmPassword !== newPassword
  const passwordFormValid =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    newPassword !== currentPassword &&
    confirmPassword === newPassword

  const newPasswordError = newPwTooShort
    ? 'Must be at least 8 characters.'
    : newPassword.length >= 8 && newPassword === currentPassword
      ? 'Choose a new password different from the current one.'
      : undefined
  const confirmError = confirmMismatch ? 'Passwords do not match.' : undefined

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!passwordFormValid) return
    setSubmitting(true)
    try {
      await changePassword({ currentPassword, newPassword })
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('')
      notify.success('Password updated.')
    } catch (err) { notify.error((err as { message?: string })?.message || 'Something went wrong. Please try again.') }
    finally { setSubmitting(false) }
  }

  const submitDisconnect = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user?.hasPassword && (!newPassword || newPassword.length < 8)) { notify.error('Password must be at least 8 characters.'); return }
    if (!user?.hasPassword && newPassword !== confirmPassword) { notify.error('Passwords do not match.'); return }
    setSubmitting(true)
    try {
      await disconnectProvider(user?.hasPassword ? '' : newPassword)
      setNewPassword(''); setConfirmPassword('')
      await refreshMe()
      notify.success('Disconnected. You are now a local user.')
    } catch (err) { notify.error((err as { message?: string })?.message || 'Something went wrong. Please try again.') }
    finally { setSubmitting(false) }
  }

  const submitPreferences = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await updatePreferences(prefs as Record<string, unknown>)
      notify.success('Preferences saved.')
    } catch (_) { notify.error('Something went wrong. Please try again.') }
    finally { setSubmitting(false) }
  }

  const SidebarItem = ({ tab, label, icon: Icon }: { tab: Tab; label: string; icon: React.ElementType }) => (
    <button
      type="button"
      onClick={() => setActiveTab(tab)}
      className={`settings-nav-item${activeTab === tab ? ' is-active' : ''}`}
      aria-current={activeTab === tab ? 'page' : undefined}
    >
      <Icon size={16} className="settings-nav-item__icon" />
      {label}
    </button>
  )

  const renderContent = () => {
    if (activeTab === 'profile') return (
      <form onSubmit={submitProfile} className="space-y-6">
        {/* Photo + Name row */}
        <div className="flex flex-col sm:flex-row gap-5">
          <div className="flex-shrink-0">
            <div className="relative group w-20 h-20">
              {avatarUrl ? (
                <img src={avatarUrl} alt="avatar" className="w-20 h-20 rounded-2xl object-cover border border-gray-200 dark:border-gray-700" />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-brand-50 dark:bg-brand-900/40 flex items-center justify-center text-2xl font-semibold text-brand-600 dark:text-brand-300">{initials}</div>
              )}
              <div className="absolute inset-0 rounded-2xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                <label className="p-2 rounded-full bg-white/20 hover:bg-white/30 cursor-pointer transition-colors" title="Change photo">
                  <Camera size={16} className="text-white" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleAvatarFileSelect(file)
                    e.target.value = ''
                  }} />
                </label>
                {avatarUrl && (
                  <button type="button" onClick={() => { setAvatarUrl(''); notify.success('Photo removed. Save to apply.') }}
                    className="p-2 rounded-full bg-white/20 hover:bg-red-500/60 transition-colors" title="Remove">
                    <Trash2 size={16} className="text-white" />
                  </button>
                )}
              </div>
              {uploadingAvatar && (
                <div className="absolute inset-0 rounded-2xl bg-black/40 flex items-center justify-center">
                  <Loader2 size={20} className="animate-spin text-white" />
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <FieldLabel htmlFor="first-name">First Name</FieldLabel>
              <input id="first-name" className="field-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div>
              <FieldLabel htmlFor="last-name">Last Name</FieldLabel>
              <input id="last-name" className="field-input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="space-y-3">
          <h4 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-[0.12em]">Contact</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <input id="email" className="field-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} disabled={isFederated(user?.provider)} />
              {isFederated(user?.provider) && <p className="mt-1.5 text-xs text-gray-500 flex items-center gap-1"><Lock size={11} /> Managed by {providerLabel(user?.provider)}</p>}
              {!isFederated(user?.provider) && user?.emailVerified === true && <p className="mt-1.5 text-xs text-emerald-600 flex items-center gap-1"><ShieldCheck size={11} /> Verified</p>}
              {!isFederated(user?.provider) && user?.emailVerified === false && <p className="mt-1.5 text-xs text-amber-600 flex items-center gap-1"><AlertCircle size={11} /> Not verified</p>}
            </div>
            <div>
              <FieldLabel htmlFor="phone">Phone</FieldLabel>
              <input id="phone" className="field-input" type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Locale */}
        <div className="space-y-3">
          <h4 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-[0.12em]">Locale</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <FieldLabel htmlFor="lang">Language</FieldLabel>
              <select id="lang" className="field-input" value={prefs.language} onChange={(e) => setPrefs({ ...prefs, language: e.target.value })}>
                {languages.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <div>
              <FieldLabel htmlFor="country">Country</FieldLabel>
              <select id="country" className="field-input" value={prefs.country} onChange={(e) => setPrefs({ ...prefs, country: e.target.value })}>
                <option value="">Select country</option>
                {countries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <FieldLabel htmlFor="tz">Timezone</FieldLabel>
              <select id="tz" className="field-input" value={prefs.timezone} onChange={(e) => setPrefs({ ...prefs, timezone: e.target.value })}>
                <option value="">Auto-detect</option>
                {timezones.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
              </select>
            </div>
          </div>
        </div>
        <FormActions
          onCancel={onClose}
          submitLabel="Save Profile"
          submittingLabel="Saving…"
          submitting={submitting}
        />
      </form>
    )

    if (activeTab === 'account') {
      const federated = isFederated(user?.provider)
      if (federated) {
        const label = providerLabel(user?.provider)
        return (
          <div className="space-y-6">
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck size={20} className="text-emerald-500 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Connected to {label}</h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Password and email are managed by {label}.</p>
                </div>
              </div>
            </div>
            <div className="border-t border-[rgb(var(--border-subtle))] pt-6">
              <SectionHeader
                icon={KeyRound}
                title={`Disconnect from ${label}`}
                description={user?.hasPassword ? 'You already have a local password. Click to disconnect.' : 'Set a password to convert to a local account.'}
              />
              <form onSubmit={submitDisconnect} className="space-y-4">
                {!user?.hasPassword && (
                  <>
                    <PasswordInput
                      id="new-password"
                      label="New password"
                      value={newPassword}
                      onChange={setNewPassword}
                      autoComplete="new-password"
                      hint="At least 8 characters with one letter and one number."
                    />
                    <PasswordInput
                      id="confirm-password"
                      label="Confirm password"
                      value={confirmPassword}
                      onChange={setConfirmPassword}
                      autoComplete="new-password"
                      error={confirmError}
                    />
                  </>
                )}
                <FormActions
                  onCancel={onClose}
                  submitLabel={user?.hasPassword ? `Disconnect from ${label}` : 'Disconnect and set password'}
                  submittingLabel="Disconnecting…"
                  submitting={submitting}
                  variant="danger"
                />
              </form>
            </div>
          </div>
        )
      }
      if (!user?.hasPassword) {
        return (
          <div className="rounded-xl border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))] p-5 text-sm text-gray-600 dark:text-gray-300">
            No local password set.
          </div>
        )
      }
      return (
        <form onSubmit={submitPassword} className="space-y-5" autoComplete="off">
          <SectionHeader
            icon={KeyRound}
            title="Change password"
            description="Update your password to keep your account secure. We recommend using a unique password of at least 8 characters."
          />
          <div className="space-y-4">
            <PasswordInput
              id="current-password"
              label="Current Password"
              value={currentPassword}
              onChange={setCurrentPassword}
              autoComplete="current-password"
              autoFocus
            />
            <PasswordInput
              id="new-password"
              label="New Password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              hint="At least 8 characters. Mix letters and numbers for extra safety."
              error={newPasswordError}
            />
            <PasswordInput
              id="confirm-password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              error={confirmError}
            />
          </div>
          <FormActions
            onCancel={onClose}
            submitLabel="Update Password"
            submittingLabel="Updating…"
            submitting={submitting}
            submitDisabled={!passwordFormValid}
          />
        </form>
      )
    }

    if (activeTab === 'security') {
      if (isFederated(user?.provider)) {
        return (
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5">
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-emerald-500 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Security managed by {providerLabel(user?.provider)}</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">2FA is handled by your {providerLabel(user?.provider)} account.</p>
              </div>
            </div>
          </div>
        )
      }
      const twoFaOn = Boolean(user?.twoFactorEnabled)

      const startSetup = async () => {
        setSubmitting(true)
        try { const s = await twoFactorSetup(); setTwoFaSetup2(s); setTwoFaCode('') }
        catch (err) { notify.error((err as { message?: string })?.message || 'Setup failed.') }
        finally { setSubmitting(false) }
      }
      const confirmEnable = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!twoFaCode || twoFaCode.length < 6) { notify.error('Enter the 6-digit code.'); return }
        setSubmitting(true)
        try {
          const r = await twoFactorEnable(twoFaCode)
          setRecoveryCodes(r.recoveryCodes || [])
          setTwoFaSetup2(null); setTwoFaCode('')
          await refreshMe()
          notify.success('2FA enabled.')
        } catch (err) { notify.error((err as { message?: string })?.message || 'Invalid code.') }
        finally { setSubmitting(false) }
      }
      const doDisable = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!disableConfirm) { notify.error('Enter password or code.'); return }
        setSubmitting(true)
        try {
          await twoFactorDisable(disableConfirm)
          setDisableConfirm('')
          await refreshMe()
          notify.success('2FA disabled.')
        } catch (err) { notify.error((err as { message?: string })?.message || 'Something went wrong. Please try again.') }
        finally { setSubmitting(false) }
      }

      if (!twoFaOn && !twoFaSetup2 && !recoveryCodes) return (
        <div className="rounded-xl border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))] p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-900/40 text-brand-600 dark:text-brand-300 flex items-center justify-center flex-shrink-0 ring-1 ring-inset ring-brand-600/20 dark:ring-brand-400/20">
              <Smartphone size={18} />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Two-factor authentication</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Add an extra layer of security with an authenticator app.</p>
              <button type="button" className="btn-primary text-sm mt-4" onClick={startSetup} disabled={submitting}>
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Setting up…</> : 'Enable 2FA'}
              </button>
            </div>
          </div>
        </div>
      )

      if (twoFaSetup2) {
        const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(twoFaSetup2.otpauthUri)}`
        return (
          <form onSubmit={confirmEnable} className="space-y-5">
            <div className="rounded-xl border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))] p-5 space-y-4 text-center">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Scan this QR code</h3>
              <img src={qrSrc} alt="2FA QR" className="w-44 h-44 bg-white p-2 rounded border mx-auto" />
              <code className="text-xs font-mono px-2 py-1 bg-[rgb(var(--surface-3))] rounded select-all">{twoFaSetup2.secret}</code>
            </div>
            <div>
              <FieldLabel htmlFor="totp-code">Enter code</FieldLabel>
              <input id="totp-code" type="text" inputMode="numeric" maxLength={6}
                className="field-input tracking-widest text-center font-mono" placeholder="123456"
                value={twoFaCode} onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, ''))} autoFocus required />
            </div>
            <FormActions
              onCancel={() => setTwoFaSetup2(null)}
              submitLabel="Verify and enable"
              submittingLabel="Verifying…"
              submitting={submitting}
            />
          </form>
        )
      }

      if (recoveryCodes) return (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-5 space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Save your recovery codes</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Shown only once. Store them safely.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm font-mono">
            {recoveryCodes.map((c) => <div key={c} className="px-2 py-1.5 bg-[rgb(var(--surface-3))] rounded text-center select-all">{c}</div>)}
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" className="btn-secondary text-sm" onClick={() => { navigator.clipboard?.writeText(recoveryCodes.join('\n')); setCopiedRecovery(true); setTimeout(() => setCopiedRecovery(false), 2000) }}>
              {copiedRecovery ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy all</>}
            </button>
            <button type="button" className="btn-primary text-sm" onClick={() => setRecoveryCodes(null)}>Done</button>
          </div>
        </div>
      )

      return (
        <form onSubmit={doDisable} className="space-y-5">
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5">
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-emerald-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">2FA is enabled</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">A code is required on every sign-in.</p>
              </div>
            </div>
          </div>
          <div>
            <FieldLabel htmlFor="disable-code">Password or TOTP code to disable</FieldLabel>
            <input id="disable-code" type="password" className="field-input" value={disableConfirm} onChange={(e) => setDisableConfirm(e.target.value)} required />
          </div>
          <FormActions
            onCancel={onClose}
            submitLabel="Disable 2FA"
            submittingLabel="Disabling…"
            submitting={submitting}
            variant="danger"
          />
        </form>
      )
    }

    // preferences
    return (
      <form onSubmit={submitPreferences} className="space-y-6">
        <div className="space-y-3">
          <h3 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-[0.12em]">Appearance</h3>
          <div className="rounded-xl border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))] p-4 space-y-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2"><Sun size={14} /> Theme</label>
            <div className="flex flex-wrap gap-2">
              {([{ v: 'system', l: 'System', I: Monitor }, { v: 'light', l: 'Light', I: Sun }, { v: 'dark', l: 'Dark', I: Moon }] as const).map(({ v, l, I }) => (
                <button key={v} type="button" onClick={() => setPrefs({ ...prefs, theme: v })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors ${prefs.theme === v ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                  <I size={14} /> {l}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))] p-4 space-y-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2"><User size={14} /> Navbar display</label>
            <div className="flex flex-wrap gap-2">
              {['Avatar', 'Initials', 'Name'].map((label) => (
                <button key={label} type="button" onClick={() => setPrefs({ ...prefs, navbarDisplay: label.toLowerCase() })}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                    prefs.navbarDisplay === label.toLowerCase()
                      ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                      : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}>{label}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-[0.12em]">Notifications</h3>
          <div className="rounded-xl border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))] p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2"><Bell size={14} /> Email notifications</label>
                <p className="text-xs text-gray-500 mt-0.5">Receive account and security updates via email.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input type="checkbox" className="sr-only peer" checked={Boolean(prefs.emailNotifications)} onChange={(e) => setPrefs({ ...prefs, emailNotifications: e.target.checked })} />
                <div className="w-10 h-5 bg-gray-300 dark:bg-gray-700 rounded-full peer peer-checked:bg-brand-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5" />
              </label>
            </div>
          </div>
        </div>

        <FormActions
          onCancel={onClose}
          submitLabel="Save Preferences"
          submittingLabel="Saving…"
          submitting={submitting}
        />
      </form>
    )
  }

  if (!isOpen) return null

  const tier = entitlements?.categoryCode && entitlements?.tierCode
    ? { category: entitlements.categoryCode, tier: entitlements.tierCode }
    : null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-0 md:p-4 overflow-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="modal-backdrop" onClick={() => !submitting && onClose()} aria-hidden="true" />

      <div
        ref={panelRef}
        tabIndex={-1}
        className="modal-panel max-w-[960px] rounded-none md:rounded-2xl"
      >
        {/* Header */}
        <header className="flex items-center gap-4 px-6 py-4 border-b border-[rgb(var(--border-subtle))]">
          {user && (
            <div className="relative flex-shrink-0">
              <div className="w-11 h-11 rounded-xl bg-brand-50 dark:bg-brand-900/40 text-brand-600 dark:text-brand-300 flex items-center justify-center text-base font-semibold overflow-hidden ring-1 ring-inset ring-brand-600/20 dark:ring-brand-400/20">
                {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : initials}
              </div>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 id={titleId} className="text-[17px] font-semibold tracking-tight text-gray-900 dark:text-gray-100 truncate">
              {displayName ? `${displayName}` : 'User Settings'}
            </h2>
            <div className="flex items-center gap-2 mt-0.5">
              {user?.email && (
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
              )}
              {tier && (
                <>
                  <span className="text-gray-300 dark:text-gray-700" aria-hidden="true">•</span>
                  <TierBadge category={tier.category} tier={tier.tier} />
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="nav-icon-btn flex-shrink-0"
            aria-label="Close settings"
          >
            <X size={18} />
          </button>
        </header>

        {/* Body */}
        <div className="flex flex-col md:flex-row">
          <aside
            className="md:w-56 border-b md:border-b-0 md:border-r border-[rgb(var(--border-subtle))] p-3 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible"
            aria-label="Settings sections"
          >
            <SidebarItem tab="profile" label="Profile" icon={User} />
            <SidebarItem tab="account" label="Account" icon={KeyRound} />
            <SidebarItem tab="security" label="Security" icon={ShieldCheck} />
            <SidebarItem tab="preferences" label="Preferences" icon={Settings} />
          </aside>
          <section className="flex-1 p-6 md:p-7 overflow-auto max-h-[72vh]">
            {renderContent()}
          </section>
        </div>
      </div>

      {cropSrc && (
        <AvatarCropper imageSrc={cropSrc} onCancel={() => setCropSrc(null)} onSave={handleCroppedAvatar} />
      )}
    </div>
  )
}
